import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  FileText, 
  ClipboardList, 
  Pill,
  Database,
  Activity,
  CalendarClock,
  RotateCcw,
  Menu,
  X,
  LogOut,
  Phone,
  Mail
} from 'lucide-react';
import { cacheService } from '../services/cacheService';
import { clearAuthStorage } from '../utils/authStorage';

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { label: 'Dashboard', path: '/', icon: 'dashboard' },
  { label: 'Users', path: '/users', icon: 'users' },
  { label: 'Doctors', path: '/doctors', icon: 'doctors' },
  { label: 'Forms', path: '/forms', icon: 'forms' },
  { label: 'Form Responses', path: '/form-responses', icon: 'responses' },
  { label: 'Prescriptions', path: '/prescriptions', icon: 'prescriptions' },
  { label: 'Metadata', path: '/metadata', icon: 'metadata' },
  { label: 'Metrics', path: '/metrics', icon: 'metrics' },
  { label: 'Scheduler Settings', path: '/scheduler-settings', icon: 'scheduler-settings' },
  { label: 'Payment Refunds', path: '/payments/refunds', icon: 'payment-refunds' },
];

const getIcon = (iconName: string) => {
  switch (iconName) {
    case 'users':
      return <Users size={20} />;
    case 'doctors':
      return <UserCheck size={20} />;
    case 'forms':
      return <FileText size={20} />;
    case 'responses':
      return <ClipboardList size={20} />;
    case 'prescriptions':
      return <Pill size={20} />;
    case 'metadata':
      return <Database size={20} />;
    case 'metrics':
      return <Activity size={20} />;
    case 'scheduler-settings':
      return <CalendarClock size={20} />;
    case 'payment-refunds':
      return <RotateCcw size={20} />;
    default:
      return <Users size={20} />;
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cacheEnabled, setCacheEnabled] = useState(cacheService.isCacheEnabled());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    clearAuthStorage();
    window.location.href = '/login';
  };

  const handleToggleCache = () => {
    cacheService.setCacheEnabled(!cacheEnabled);
    setCacheEnabled(cacheService.isCacheEnabled());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="admin-layout">
      {/* Mobile menu button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-md shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1 className="sidebar-title">Swasth Bharat</h1>
          <p className="sidebar-subtitle">Admin Portal</p>
        </div>

        <nav>
          <ul className="nav-menu">
            {navItems.map((item) => (
              <li key={item.path} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="nav-icon">{getIcon(item.icon)}</span>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Top navbar */}
        <header className="top-navbar">
          <h1 className="page-title">
            {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
          </h1>
          <div className="user-info relative" ref={dropdownRef}>
            <div className="user-avatar cursor-pointer" onClick={() => setDropdownOpen((v) => !v)}>
              {currentUser.first_name?.charAt(0) || 'A'}
            </div>
            <div className="user-details cursor-pointer" onClick={() => setDropdownOpen((v) => !v)}>
              <span className="user-name">
                {currentUser.first_name ? `${currentUser.first_name} ${currentUser.last_name}` : 'Admin User'}
              </span>
              <span className="user-role">{currentUser.role || 'Administrator'}</span>
            </div>
            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="user-dropdown">
                {/* User contact info */}
                <div className="user-dropdown-info">
                  {currentUser.email && (
                    <div className="user-dropdown-contact">
                      <Mail size={14} />
                      <span>{currentUser.email}</span>
                    </div>
                  )}
                  {currentUser.mobile_number && (
                    <div className="user-dropdown-contact">
                      <Phone size={14} />
                      <span>{currentUser.mobile_number}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { handleLogout(); setDropdownOpen(false); }}
                  className="user-dropdown-btn"
                >
                  <LogOut size={16} /> Logout
                </button>
                <button
                  onClick={() => { handleToggleCache(); setDropdownOpen(false); }}
                  className="user-dropdown-btn"
                >
                  {cacheEnabled ? 'Disable Caching' : 'Enable Caching'}
                </button>
                <div className="user-dropdown-status">
                  Caching is <span className={cacheEnabled ? 'text-green-600' : 'text-red-600'}>{cacheEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="content">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout; 